import classNames from 'classnames'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Body1, Body2, Title2 } from '@ui/Typography/Typography'
import { FileUploadResult } from '@ui/components/FileUpload/FileUploadResult'
import { FileUploadSelect } from '@ui/components/FileUpload/FileUploadSelect'
import { AlertIcon } from '@ui/icons/Alert.icon'
import { PlusIcon } from '@ui/icons/Plus.icon'

import { useWindowResize } from '@utils/domUtils'

import { DocumentCategorySizeDto, DocumentCategoryType } from '@model/DocumentCategories'
import { DocumentClientAreaDto } from '@model/DocumentClientAreaDto'

import { Loading } from '@shared/components/Loading/Loading'

import { FileData, FileExtension, getFileExtensions } from './types'

import styles from './FileUpload.module.scss'

interface FileUploadSingleProps {
  multiple?: false
  file?: FileData
  files?: never
  onUpload: (file: FileData) => void
}

interface FileUploadMultipleProps {
  multiple: true
  file?: never
  files?: FileData[]
  onUpload: (files: FileData[]) => void
}

interface FileUploadCommonProps {
  title?: string
  isLoading?: boolean
  acceptType?: FileExtension[]
  disabled?: boolean
  documentCategory: number
  categorySizes?: DocumentCategorySizeDto[]
  onRemove?: (file: FileData) => void
  onBeforeRemove?: (file: FileData) => void
  errorMessage?: string
  buttonText?: string
  maxFiles?: number
  documents?: DocumentClientAreaDto[]
  needRefresh?: boolean
}
export type FileUploadProps = FileUploadCommonProps &
  (FileUploadSingleProps | FileUploadMultipleProps)

export const FileUpload: React.FC<FileUploadProps> = ({
  file,
  files,
  multiple,
  title,
  isLoading = false,
  acceptType = getFileExtensions(),
  disabled = false,
  onUpload,
  onRemove,
  onBeforeRemove,
  buttonText,
  documentCategory,
  categorySizes = [],
  maxFiles = 20,
  needRefresh,
}) => {
  const { t } = useTranslation()
  const [contextCategorySize, setContextCategorySize] = useState<number>(4)
  const [addedFiles, setAddedFiles] = useState<FileData[]>([])
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const canAddMore = !disabled && (!multiple || !maxFiles || addedFiles.length < maxFiles)

  useEffect(() => {
    const category = categorySizes.find((c) => c.id === documentCategory)
    setContextCategorySize(category?.maxUploadFileSizeInMb ?? 4)
  }, [categorySizes, documentCategory])
  const isMobile = useWindowResize()

  useEffect(() => {
    if (multiple && files) setAddedFiles(files)
    else if (!multiple && file) setAddedFiles([file])
    else setAddedFiles([])
  }, [files, file, multiple, needRefresh])

  const handleUpload = async (uploadedFile: FileData) => {
    const index = addedFiles.findIndex((f) => f.id === uploadedFile.id)

    const updatedFile: FileData = { ...uploadedFile, error: false, errorMessage: undefined }

    if (!isFileNameValid(uploadedFile.fileName)) {
      updatedFile.error = true
      updatedFile.errorMessage = t(
        'Error in file name, characters allowed: a-z A-Z 0-9 - _ ( ) . ,'
      )
    }

    if (!isTypeValid(uploadedFile.fileType, acceptType)) {
      updatedFile.error = true
      updatedFile.errorMessage =
        t('Error in file type. Supported format: ') +
        acceptType.map((x) => x.toUpperCase()).join(', ')
    }

    if (!isFileSizeValid(uploadedFile.fileSize, contextCategorySize)) {
      updatedFile.error = true
      updatedFile.errorMessage = t('Error in file size', {
        name: uploadedFile.fileName,
        size: contextCategorySize,
      })
    }

    if (!updatedFile.error) {
      updatedFile.base64Content = await toBase64(uploadedFile.data)
    }

    const cleanAddedFiles = addedFiles.filter(Boolean)
    if (multiple) {
      const newFiles = index >= 0 ? [...cleanAddedFiles] : [...cleanAddedFiles, updatedFile]
      if (index >= 0) newFiles[index] = updatedFile
      setAddedFiles(newFiles)
      onUpload?.(newFiles)
    } else {
      setAddedFiles([updatedFile])
      onUpload?.(updatedFile)
    }
  }

  const handleRemove = (fileToRemove: FileData) => {
    onBeforeRemove?.(fileToRemove)
    if (multiple) {
      const updated = addedFiles.filter((f) => f.id !== fileToRemove.id)
      setAddedFiles(updated)
      onRemove?.(fileToRemove)
    } else {
      setAddedFiles([])
      onRemove?.(fileToRemove)
    }
  }

  return (
    <Loading isLoading={isLoading} showLoadingIcon text={t('Uploading ...')}>
      <div className={styles.newFileUploadWrapper}>
        {title && <Title2 medium>{title}</Title2>}
        {isMobile && !multiple && !title && <Title2 medium>{t('Upload Files')}</Title2>}
        {multiple && !title && (
          <Title2 medium>
            {t('Upload up to files', {
              count: maxFiles,
            })}
          </Title2>
        )}
        {isMobile && (
          <Body1>
            <div className={styles.restrictions}>
              {t('Upload restrictions', {
                size: contextCategorySize,
                types: acceptType.map((x) => x.toUpperCase()).join(', '),
              })}
            </div>
          </Body1>
        )}
        {!isMobile && (
          <div className={styles.wrapper}>
            <FileUploadSelect
              acceptType={acceptType}
              maxFileSize={contextCategorySize}
              onUpload={handleUpload}
              disabled={!canAddMore || disabled}
              buttonText={buttonText}
            />
          </div>
        )}

        {addedFiles.some((f) => f.errorMessage) && (
          <div>
            {addedFiles
              .filter((f) => f.errorMessage)
              .map((f) => (
                <div className={styles.errorHandlingWrapper} key={f.id}>
                  <AlertIcon color={'text-danger-med-em'} />
                  <Body2 color='text-danger-med-em'>{f.errorMessage}</Body2>
                </div>
              ))}
          </div>
        )}

        {addedFiles.map((f) => (
          <FileUploadResult
            key={f.id}
            file={f}
            error={f.errorMessage}
            disabled={disabled}
            isLoading={isLoading}
            onUpload={handleUpload}
            onRemove={() => handleRemove(f)}
          />
        ))}

        {(isMobile || (multiple && addedFiles.length > 0)) && (
          <div
            onClick={() => {
              if (canAddMore && !disabled) fileInputRef.current?.click()
            }}
            className={classNames(
              styles.addFileWrapper,
              !canAddMore && styles.addFileWrapperDisabled
            )}
          >
            <div className={styles.fileAddNewIconWrapper}>
              <PlusIcon size={20} color='text-inverse-black-neutral' />
            </div>

            <div className={styles.descriptionWrapper}>
              <Title2 medium>{t('Add files')}</Title2>
            </div>

            <input
              ref={fileInputRef}
              type='file'
              accept={acceptType.map((x) => `.${x}`).join(',')}
              style={{ display: 'none' }}
              onChange={(e) => {
                const selected = e.target.files?.[0]
                if (selected) {
                  handleUpload(createFileData(selected))
                  e.target.value = ''
                }
              }}
            />
          </div>
        )}
      </div>
    </Loading>
  )
}

const isFileNameValid = (fileName: string) => /^[a-zA-Z0-9-_()., ]+$/.test(fileName)

const isTypeValid = (fileType: string, fileExtensions: string[]) =>
  fileExtensions.includes(fileType?.toLowerCase())

export const isFileSizeValid = (fileSize: number, maxFileSizeMb: number) =>
  fileSize / 1024 / 1024 <= maxFileSizeMb

const getFileExtension = (file: File) => {
  const parts = file.name.split('.')
  return parts[parts.length - 1].toLowerCase()
}

const toBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = (error) => reject(error)
  })

export const createFileData = (file: File): FileData => {
  const fileSizeMb = file.size / 1024 / 1024
  const fileSizeKb = file.size / 1024

  return {
    id: crypto.randomUUID(),
    fileName: file.name,
    fileType: getFileExtension(file),
    fileSize: file.size,
    fileSizeBytes: file.size,
    fileSizeKb: fileSizeKb.toFixed(2),
    fileSizeMb: fileSizeMb.toFixed(2),
    base64Content: '',
    data: file,
    error: false,
  }
}
const isPersonalOrAddressDocument = (
  documentCategory: DocumentCategoryType | undefined
): boolean => {
  if (!documentCategory) {
    return false
  }

  return [DocumentCategoryType.Address, DocumentCategoryType.Personal].includes(documentCategory)
}
export const isFileDuplicated = (
  uploadedDocuments: DocumentClientAreaDto[] | undefined,
  files: FileData[],
  file?: FileData,
  checkByName = false,
  immediateReturn = false,
  documentCategory?: number
): boolean => {
  if (!file) return false
  if (!isPersonalOrAddressDocument(documentCategory)) {
    return false
  }
  const isUploadDuplicate = (document: DocumentClientAreaDto): boolean =>
    document.name === file.fileName && documentCategory === document.category.id

  const isDuplicate = (existingFile: FileData): boolean => {
    const isSameFileName = existingFile.fileName === file.fileName
    if (!isSameFileName) {
      return false
    }

    if (checkByName) {
      return (
        existingFile.fileSize === file.fileSize && existingFile.base64Content === file.base64Content
      )
    }

    return (
      existingFile.fileSize === file.fileSize && existingFile.base64Content === file.base64Content
    )
  }

  if (
    uploadedDocuments &&
    uploadedDocuments.length > 0 &&
    uploadedDocuments.some(isUploadDuplicate)
  ) {
    return true
  }

  return immediateReturn ? files.some(isDuplicate) : files.filter(isDuplicate).length >= 2
}
export const removeUploadedFile = (addedFiles: FileData[], file?: FileData): FileData[] => {
  if (!file) return addedFiles
  return addedFiles.filter((x) => x.id !== file.id)
}
export default FileUpload
